import { forwardRef, useState } from 'react'

import { Pressable, Select, colors as zetColors } from '@zet/design-system'
import { Stack, XStack } from '@zet/design-system/layout'
import { B1, B2, B4, B5, CTN, SH3 } from '@zet/design-system/typography'

import { brandsI18n } from '../../../localization'
import { radioButtonDescription, radioButtonMessage } from '../../../utils'
import { ICON_MAP } from '../../../utils/iconMap'
import AccountAggregratorBottomSheet from '../accountAggregratorBottomsheet'

interface Props {
  label?: string
  onChange: (...props: any) => void
  onBlur: (...props: any) => void
  isRequired?: boolean
  fieldOptions: { optionValue: string; optionId: any; icon?: string }[]
  value: any
  errorMessage?: string
  ocFieldCode?: string
  optionStyleProps: any
  state: any
}

export const RadioButton: React.FC<Props> = forwardRef(
  (
    {
      label,
      onChange,
      onBlur,
      value,
      errorMessage,
      isRequired,
      fieldOptions,
      ocFieldCode,
      optionStyleProps,
      state,
    },
    ref
  ) => {
    const [isBottomSheetVisible, setBottomSheetVisible] = useState(false)
    const closeBottomSheet = () => {
      setBottomSheetVisible(false)
    }
    const openBottomSheet = () => {
      setBottomSheetVisible(true)
    }

    const adjustedFieldCode =
      state === 'PL_ADDRESS_DETAILS' ? `PL_${ocFieldCode}` : ocFieldCode

    const findRadioButtonProperties = (code: string) => {
      if (
        ocFieldCode &&
        radioButtonDescription &&
        Object.prototype.hasOwnProperty.call(
          radioButtonDescription,
          ocFieldCode
        )
      ) {
        return radioButtonDescription[adjustedFieldCode].find(
          (item) => item.code === code
        )
      }
      return null
    }

    const findRadioButtonMessage = () =>
      ocFieldCode !== undefined &&
      radioButtonMessage &&
      Object.prototype.hasOwnProperty.call(radioButtonMessage, ocFieldCode)
        ? radioButtonMessage[adjustedFieldCode]
        : null

    const radioButtomTopMessage = findRadioButtonMessage()
    const RadioButtomTopMessageIcon =
      ICON_MAP[radioButtomTopMessage?.icon || '']

    return (
      <>
        {radioButtomTopMessage && (
          <XStack className="bg-primary-10 mb-5 items-center rounded-[4px] px-2 py-2">
            {radioButtomTopMessage?.icon && <RadioButtomTopMessageIcon />}
            <B5 className="ml-2">{radioButtomTopMessage?.text}</B5>
          </XStack>
        )}
        <Stack>
          <XStack>
            {!!label && <B2 className="text-grays-80 mb-1">{label}</B2>}
            {isRequired && <B1 className="text-error-100 pl-1">*</B1>}
          </XStack>
          <Stack>
            <Select
              value={value}
              mode={'single'}
              onChange={(value) => {
                onChange(value)
                onBlur()
              }}
            >
              {fieldOptions?.map((item) => (
                <Select.Option
                  key={item?.optionId}
                  name={item?.optionId}
                  displayText={item?.optionValue}
                  selectedValue={value}
                  toggleCenter={false}
                  optionContainerStyle={`mt-3 w-full ${
                    value === item?.optionId
                      ? 'border border-primary-50'
                      : 'border border-grays-40'
                  } items-start justify-center rounded-lg py-2 px-1`}
                  showToggle={true}
                  optionTextStyle={'w-[95%] pl-3 text-base font-semibold'}
                  stylingOverrides={{ innerContainer: 'w-full' }}
                  iconColor={{
                    active: zetColors.primary[50],
                    inActive: zetColors.grays[60],
                  }}
                  backgroundColor={{
                    active: 'bg-primary-10',
                    inActive: 'bg-grays-20',
                  }}
                  textColor={{
                    active: 'text-primary-200',
                    inActive: 'text-grays-80',
                  }}
                  {...optionStyleProps}
                >
                  <Stack className="w-[100%]">
                    <XStack className="w-[100%] items-center justify-between">
                      <SH3
                        className={`${
                          value === item?.optionId
                            ? 'text-primary-200'
                            : 'text-grays-80'
                        }`}
                      >
                        {item.optionValue}
                      </SH3>
                      {findRadioButtonProperties(item?.optionId)
                        ?.recommended && (
                        <CTN className="text-primary-150 border-primary-50 mb-1 mr-3 rounded-full border bg-white px-2">
                          {brandsI18n.t('hdfc.recommended')}
                        </CTN>
                      )}
                    </XStack>
                    <B5 className="text-grays-70 w-70 font-normal">
                      {findRadioButtonProperties(item?.optionId)?.description}
                    </B5>
                    {findRadioButtonProperties(item?.optionId)
                      ?.showAccountAggregatorBottomsheet && (
                      <Pressable
                        onPress={openBottomSheet}
                        className="w-[200px]"
                      >
                        <B4 className="text-primary-100">
                          {brandsI18n.t('hdfc.accountAggregator')}
                        </B4>
                      </Pressable>
                    )}
                  </Stack>
                </Select.Option>
              ))}
            </Select>
          </Stack>
          {!!errorMessage && <B5 className="text-error-100">{errorMessage}</B5>}
        </Stack>
        <AccountAggregratorBottomSheet
          isVisible={isBottomSheetVisible}
          closeBottomSheet={closeBottomSheet}
        />
      </>
    )
  }
)
